import { useEffect, useRef, useState } from 'react';
import type { LifeAreaDTO, ObligationDTO, ObligationPriority } from '@almanac/shared';
import {
  formatTimeForDisplay,
  isAcknowledgedToday,
  isOverdue,
  liveObligationTitle,
  useCompleteObligation,
  useCreateObligation,
  useDeleteObligation,
  useNowMinute,
  useReopenObligation,
  useUpdateObligation,
} from '../lib/obligations.js';
import { usePeople } from '../lib/people.js';
import { useUndoToast } from '../lib/undoToast.js';

interface Props {
  obligation: ObligationDTO;
  lifeArea?: LifeAreaDTO;
  onEdit: (id: string) => void;
  /** When true, the task meta line skips the life-area name. Used by
   *  the Today dashboard, where rows sit inside an "Obligations" card
   *  whose header already conveys the domain — the badge becomes
   *  redundant and visual noise inside that frame. The recurring
   *  marker and the High priority flag still render. Occasion rows
   *  are unaffected (their meta carries essential info — "Birthday ·
   *  turns 73"). Also used by the Areas perspective, whose group
   *  heading already names (and colours) the area. */
  hideLifeArea?: boolean;
  /** The life area's identity colour (a `var(--area-N)` value) — when
   *  set, a small dot precedes the area name in the meta line. */
  areaColor?: string;
}

// Each action button is rem-sized so reveal width scales with the
// user's font preferences; total reveal width is sum of all actions.
const ACTION_WIDTH_REM = 5;
// Priority buttons are narrower — three of them sit on the left and we
// don't want them to swallow the whole row when revealed.
const PRIORITY_ACTION_WIDTH_REM = 3.25;
// How far the user has to drag before we lock to horizontal (and
// commit to a swipe rather than allowing vertical scroll to take
// over). Small enough that intentional swipes feel instant; large
// enough that an accidental finger jitter on a tap doesn't trip it.
const AXIS_LOCK_PX = 8;
// Hold this long without a meaningful move and we toggle sharing.
// iOS uses ~500ms as the default for long-press recognition.
const LONG_PRESS_MS = 500;
// If the finger drifts more than this between touchstart and the
// long-press deadline, we treat it as a tap-or-drag rather than a
// hold and cancel it.
const LONG_PRESS_SLOP_PX = 6;

const PRIORITY_LABEL: Record<ObligationPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

/** A row in any obligation list. Wraps the existing row layout in a
 *  swipe-action shell that reveals one or two contextual actions
 *  (Snooze, Delete) on a left-swipe, iOS Mail-style. The row also
 *  remains tappable (opens the preview card) and the check button on
 *  the left still marks complete with the existing undo toast.
 *
 *  Swipe mechanics:
 *  - `touch-action: pan-y` on the shell tells the browser to let our
 *    JS own horizontal gestures while vertical scroll still works.
 *  - We pick an axis on the first 8px of movement; once locked
 *    horizontal, the gesture won't degrade into a scroll if the
 *    finger drifts vertically partway through.
 *  - On release, the row snaps to fully-open (-totalActionsWidth) if
 *    dragged past half the actions width, otherwise back to closed.
 *  - A document-level pointerdown listener closes any open row when
 *    the user taps elsewhere on the page — including another row.
 *  - The `wasDragging` ref shields the row's onClick handlers so a
 *    drag-and-release doesn't end up opening the preview card.
 */
export function ObligationListItem({
  obligation,
  lifeArea,
  onEdit,
  hideLifeArea,
  areaColor,
}: Props) {
  const complete = useCompleteObligation();
  const reopen = useReopenObligation();
  const del = useDeleteObligation();
  const create = useCreateObligation();
  const update = useUpdateObligation();
  const undo = useUndoToast();
  const people = usePeople();
  // Re-renders this row once a minute (and on tab focus/visibility),
  // so the right-side label can flip from black → red the moment a
  // timed `today HH:MM am` task crosses its due time.
  const now = useNowMinute();

  const isCompleted = obligation.status === 'completed';
  const isOccasion = obligation.kind === 'occasion';
  // Recurring items (occasions, weekly tasks etc.) stay `active` after
  // completion — only their `nextDueDate` bumps. Without an explicit
  // "you did this today" state, the row looks identical seconds after
  // the check is tapped. `acknowledgedToday` carries the same-day
  // memory of the acknowledgment so the row can show its state.
  const acknowledgedToday = !isCompleted && isAcknowledgedToday(obligation);
  const isHighPriority = !isOccasion && obligation.priority === 'high' && !isCompleted;
  // Priority is a task-only concept; occasions and completed rows
  // don't expose the right-swipe priority panel (or the long-press
  // cycle).
  const canSetPriority = !isOccasion && !isCompleted;
  // Shared items carry a small "Shared" pill so it's visible at a
  // glance which rows the other household member can also see. Private
  // (the default) shows nothing — no pill is the quiet norm.
  const isShared = obligation.visibility === 'shared';
  const title = liveObligationTitle(obligation, people.data);
  // Past-tense verb used in the undo toast and aria labels — "complete"
  // doesn't fit a birthday, but the mechanic (bump nextDueDate to next
  // occurrence, keep active) is identical.
  const verb = isOccasion ? 'acknowledged' : 'complete';

  const rowClasses = [
    'list-item',
    isOccasion ? 'list-item--occasion' : '',
    isHighPriority ? 'list-item--priority-high' : '',
    acknowledgedToday ? 'list-item--acknowledged' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // ── Swipe + long-press state ──────────────────────────
  const shellRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  // Long-press fires after this many ms of an essentially-stationary
  // touch on a task row. ~500ms is the iOS convention for a long
  // press; shorter felt twitchy, longer felt sluggish in testing.
  const longPressTimer = useRef<number | null>(null);
  const wasLongPress = useRef(false);
  const startTranslate = useRef(0);
  const axisLock = useRef<'horizontal' | 'vertical' | null>(null);
  const wasDragging = useRef(false);

  // Build the contextual action set. Snooze is meaningful only for
  // active, non-occasion rows — occasions are pinned to a calendar
  // date and "Done today" recurring tasks shouldn't bump their next
  // occurrence into the future on a casual swipe. Delete always
  // applies; the undo toast covers the destructive ambiguity.
  const actions: SwipeAction[] = [];
  if (!isCompleted && !isOccasion && !acknowledgedToday) {
    actions.push({
      key: 'snooze',
      label: 'Tomorrow',
      kind: 'neutral',
      onActivate: () => snoozeToTomorrow(),
    });
  }
  actions.push({
    key: 'delete',
    label: 'Delete',
    kind: 'destructive',
    onActivate: () => deleteWithUndo(),
  });
  const totalActionsWidth = actions.length * ACTION_WIDTH_REM;
  // Cap drag at 1.15× to give a small "rubber band" feel past the
  // fully-open position; the snap on release pulls it back to the
  // canonical -totalActionsWidth.
  const maxOpenPx = -totalActionsWidth * 16; // rem→px assuming 16px base
  const minOpenPx = maxOpenPx * 1.15;

  // Right-swipe reveals quick priority options on the left of the row.
  // Task-only — occasions/completed rows have no priority, so the
  // right swipe is disabled for them (priorityOpenPx = 0).
  const priorityActions: PriorityAction[] = canSetPriority
    ? [
        { level: 'low', label: 'Low' },
        { level: 'medium', label: 'Med' },
        { level: 'high', label: 'High' },
      ]
    : [];
  const priorityActionsWidth = priorityActions.length * PRIORITY_ACTION_WIDTH_REM;
  const priorityOpenPx = priorityActionsWidth * 16; // positive = swiped right
  const maxRightPx = priorityOpenPx * 1.15;

  function close() {
    setTranslateX(0);
  }

  // Close when the user taps outside the row while it's open. Without
  // this, a swiped-open row stays open indefinitely and looks broken
  // when the user moves on.
  useEffect(() => {
    if (translateX === 0) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (target && shellRef.current && !shellRef.current.contains(target)) {
        close();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [translateX]);

  // Long-press toggles whether the item is shared with the household.
  // Applies to any obligation — sharing is independent of kind and
  // completion. Reversible via the undo toast.
  function toggleSharing() {
    const previous = obligation.visibility;
    const next = previous === 'shared' ? 'private' : 'shared';
    update.mutate({ id: obligation.id, input: { visibility: next } });
    undo.show(next === 'shared' ? 'Shared with the household' : 'Made private', () => {
      update.mutate({ id: obligation.id, input: { visibility: previous } });
    });
  }

  // Set an explicit priority from the right-swipe panel. Closes the
  // row, no-ops if unchanged, with an undo toast.
  function setPriorityTo(level: ObligationPriority) {
    close();
    if (level === obligation.priority) return;
    const previous = obligation.priority;
    update.mutate({ id: obligation.id, input: { priority: level } });
    undo.show(`Priority set to ${PRIORITY_LABEL[level]}`, () => {
      update.mutate({ id: obligation.id, input: { priority: previous } });
    });
  }

  function clearLongPressTimer() {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function onTouchStart(e: React.TouchEvent<HTMLDivElement>) {
    if (e.touches.length !== 1) return;
    startX.current = e.touches[0]!.clientX;
    startY.current = e.touches[0]!.clientY;
    startTranslate.current = translateX;
    axisLock.current = null;
    wasDragging.current = false;
    wasLongPress.current = false;
    setDragging(false);
    // Arm the long-press timer on every row that isn't mid-swipe.
    // The timer is cancelled by any meaningful finger movement
    // (touchmove) or touch release (touchend) before the deadline.
    //
    // Firing the timer also sets the `wasLongPress` flag, which
    // shields the body's onClick from opening the preview card — so a
    // press-and-hold toggles sharing without also popping the card.
    // Normal taps still open the card; the suppression only kicks in
    // when the touch exceeded the long-press deadline.
    clearLongPressTimer();
    if (translateX === 0) {
      longPressTimer.current = window.setTimeout(() => {
        longPressTimer.current = null;
        wasLongPress.current = true;
        toggleSharing();
      }, LONG_PRESS_MS);
    }
  }

  function onTouchMove(e: React.TouchEvent<HTMLDivElement>) {
    if (e.touches.length !== 1) return;
    const dx = e.touches[0]!.clientX - startX.current;
    const dy = e.touches[0]!.clientY - startY.current;

    // Any drift past the slop cancels the long-press — the user is
    // either tapping (which won't trigger the timer anyway) or
    // starting a swipe.
    if (
      longPressTimer.current !== null &&
      (Math.abs(dx) > LONG_PRESS_SLOP_PX || Math.abs(dy) > LONG_PRESS_SLOP_PX)
    ) {
      clearLongPressTimer();
    }

    if (axisLock.current === null) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      axisLock.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (axisLock.current === 'horizontal') {
        setDragging(true);
        wasDragging.current = true;
      }
    }

    if (axisLock.current !== 'horizontal') return;
    // Clamp to [minOpenPx, maxRightPx]: leftward reveals snooze/delete,
    // rightward reveals the priority panel. Both ends allow a small
    // overshoot for tactility. maxRightPx is 0 on rows without priority
    // (occasions/completed), so they stay left-swipe-only.
    const target = Math.max(minOpenPx, Math.min(maxRightPx, startTranslate.current + dx));
    setTranslateX(target);
  }

  function onTouchEnd() {
    clearLongPressTimer();
    if (axisLock.current === 'horizontal') {
      // Past half of either side snaps fully open in that direction;
      // otherwise spring back closed.
      if (translateX <= maxOpenPx / 2) {
        setTranslateX(maxOpenPx);
      } else if (priorityOpenPx > 0 && translateX >= priorityOpenPx / 2) {
        setTranslateX(priorityOpenPx);
      } else {
        setTranslateX(0);
      }
    }
    setDragging(false);
    axisLock.current = null;
    // wasDragging stays true through the synthetic click that follows
    // touchend, then resets on next touchstart. Without that, the
    // body button's onClick would fire after a swipe gesture.
    // Same for wasLongPress — it shields the body's onClick from
    // firing right after a long-press has already done its work.
  }

  function handleCheck(e: React.MouseEvent) {
    e.stopPropagation();
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    if (wasLongPress.current) {
      wasLongPress.current = false;
      return;
    }
    if (isCompleted) {
      reopen.mutate({ id: obligation.id, nextDueDate: obligation.dueDate });
      return;
    }
    const previousNextDueDate = obligation.nextDueDate ?? obligation.dueDate;
    const id = obligation.id;
    complete.mutate({ id });
    undo.show(`Marked “${title}” ${verb}`, () => {
      reopen.mutate({ id, nextDueDate: previousNextDueDate });
    });
  }

  function handleBody() {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    if (wasLongPress.current) {
      // Long-press already cycled priority; swallow the synthetic
      // click so the preview card doesn't also pop up.
      wasLongPress.current = false;
      return;
    }
    if (translateX !== 0) {
      // A tap on the body while actions are revealed should dismiss
      // the swipe rather than open the preview card — matches how
      // iOS Mail closes a swiped row.
      close();
      return;
    }
    onEdit(obligation.id);
  }

  function snoozeToTomorrow() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    // Snooze updates only nextDueDate — the anchor dueDate and the
    // recurrence rule both stay put, so a recurring task continues
    // to fire on its original cadence after this one occurrence is
    // pushed. Same semantics as the preview card's Snooze chips.
    update.mutate({ id: obligation.id, input: { nextDueDate: iso } });
    close();
  }

  function deleteWithUndo() {
    // Snapshot the row before the mutation drops it from the cache,
    // so the undo handler can recreate the obligation with all its
    // original fields — including the same id, so any cross-record
    // references (audit log entries, person links) keep pointing at
    // the right entity.
    const snapshot = obligation;
    del.mutate({ id: snapshot.id });
    close();
    undo.show(`Deleted “${title}”`, () => {
      create.mutate({
        id: snapshot.id,
        title: snapshot.title,
        description: snapshot.description ?? null,
        lifeAreaId: snapshot.lifeAreaId,
        dueDate: snapshot.dueDate.slice(0, 10),
        recurrenceRule: snapshot.recurrenceRule ?? null,
        priority: snapshot.priority,
        reminderDaysBefore: snapshot.reminderDaysBefore ?? null,
        notes: snapshot.notes ?? null,
        visibility: snapshot.visibility,
        kind: snapshot.kind,
        occasionType: snapshot.occasionType ?? null,
        occasionLabel: snapshot.occasionLabel ?? null,
        anchorYear: snapshot.anchorYear ?? null,
        personIds: snapshot.personIds,
      });
    });
  }

  // A filled check belongs to anything that's "done for now" — fully
  // completed one-offs, and recurring items whose latest acknowledgment
  // was today. Tomorrow the acknowledged styling lifts and the next
  // occurrence is what shows.
  const checkFilled = isCompleted || acknowledgedToday;
  const dueLabel = acknowledgedToday
    ? isOccasion
      ? 'Acknowledged today'
      : 'Done today'
    : formatDueLabel(obligation);
  const dueClass = acknowledgedToday
    ? 'list-item__action--acknowledged'
    : relativeClass(obligation, now);

  return (
    <div
      ref={shellRef}
      className={`swipe-shell ${dragging ? 'swipe-shell--dragging' : ''} ${translateX !== 0 ? 'swipe-shell--open' : ''}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className="swipe-shell__actions"
        style={{ width: `${totalActionsWidth}rem` }}
        aria-hidden={translateX >= 0}
      >
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`swipe-shell__action swipe-shell__action--${a.kind}`}
            // tabIndex flips with reveal state so the action buttons
            // aren't reachable by keyboard until the row is actually
            // swiped open — keyboard users will use the preview card
            // or the existing Edit drawer to delete / snooze.
            tabIndex={translateX < 0 ? 0 : -1}
            onClick={a.onActivate}
          >
            {a.label}
          </button>
        ))}
      </div>
      {priorityActions.length > 0 && (
        <div
          className="swipe-shell__actions swipe-shell__actions--left"
          style={{ width: `${priorityActionsWidth}rem` }}
          aria-hidden={translateX <= 0}
        >
          {priorityActions.map((a) => (
            <button
              key={a.level}
              type="button"
              className={`swipe-shell__action swipe-shell__action--priority swipe-shell__action--prio-${a.level} ${a.level === obligation.priority ? 'swipe-shell__action--prio-active' : ''}`}
              tabIndex={translateX > 0 ? 0 : -1}
              aria-pressed={a.level === obligation.priority}
              onClick={() => setPriorityTo(a.level)}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      <div
        className={rowClasses}
        role="listitem"
        style={{ transform: `translateX(${translateX}px)` }}
      >
        <button
          type="button"
          className={`list-item__check ${checkFilled ? 'list-item__check--done' : ''}`}
          onClick={handleCheck}
          aria-label={isCompleted ? `Reopen ${title}` : `Mark ${title} ${verb}`}
          aria-pressed={checkFilled}
          title={
            isCompleted
              ? 'Click to reopen'
              : isOccasion
                ? 'Click to acknowledge'
                : 'Click to mark complete'
          }
        >
          {checkFilled ? '✓' : ''}
        </button>
        <button
          type="button"
          className="list-item__body"
          onClick={handleBody}
          aria-label={`Edit ${title}`}
        >
          <div
            className={`list-item__title ${isCompleted || acknowledgedToday ? 'list-item__title--done' : ''}`}
          >
            {title}
          </div>
          <div className="list-item__meta">
            {(() => {
              const metaNode = isOccasion
                ? formatOccasionMeta(obligation)
                : formatTaskMeta(obligation, lifeArea, hideLifeArea, areaColor);
              return (
                <>
                  {metaNode}
                  {isShared && (
                    <>
                      {metaNode != null && ' '}
                      <span
                        className="shared-pill"
                        aria-label="Shared with the household"
                        title="Shared with the household"
                      >
                        S
                      </span>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </button>
        <div className={`list-item__action ${dueClass}`}>
          {isHighPriority && <PriorityFlag />}
          {dueLabel}
        </div>
      </div>
    </div>
  );
}

interface SwipeAction {
  key: string;
  label: string;
  kind: 'neutral' | 'destructive';
  onActivate: () => void;
}

interface PriorityAction {
  level: ObligationPriority;
  label: string;
}

// Recurring used to be spelled out as the word "recurring" in the meta
// line. On a crowded row (life area + recurring + a priority pill + the
// Shared badge) that word was wide enough to wrap the line in two. A
// glyph carries the same meaning in a fraction of the width; a
// visually-hidden label keeps it legible to screen readers and in the
// row's accessible name.
function RecurringIcon() {
  return (
    <span className="meta-recur">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
        <polyline
          points="17 1 21 5 17 9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 11V9a4 4 0 0 1 4-4h14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points="7 23 3 19 7 15"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M21 13v2a4 4 0 0 1-4 4H3"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="sr-only">Recurring</span>
    </span>
  );
}

function formatTaskMeta(
  o: ObligationDTO,
  lifeArea?: LifeAreaDTO,
  hideLifeArea?: boolean,
  areaColor?: string,
): React.ReactNode {
  // With the life-area suppressed, "recurring" leads instead of
  // following a dot. Same for the priority pill: it stands alone
  // rather than tagging onto a "Personal · recurring" prefix.
  const showRecurring = Boolean(o.recurrenceRule);
  // High priority renders as a small flag beside the due label (see
  // the row's action slot) — loud enough to scan for, without a solid
  // pill widening the meta line. Low stays a quiet outlined pill here:
  // "this isn't urgent" belongs with the descriptive meta. Medium is
  // the unmarked default.
  const showLowPill = o.priority === 'low';
  const priorityPill = showLowPill ? (
    <span className="list-item__priority-pill list-item__priority-pill--low">Low</span>
  ) : null;
  if (hideLifeArea) {
    if (!showRecurring && !priorityPill) return null;
    return (
      <>
        {showRecurring && <RecurringIcon />}
        {priorityPill && (
          <>
            {showRecurring && ' · '}
            {priorityPill}
          </>
        )}
      </>
    );
  }
  return (
    <>
      {areaColor && <span className="area-dot" style={{ background: areaColor }} aria-hidden />}
      {lifeArea?.name ?? 'Personal'}
      {showRecurring && (
        <>
          {' · '}
          <RecurringIcon />
        </>
      )}
      {priorityPill && <> {priorityPill}</>}
    </>
  );
}

/** The High-priority marker — a small terracotta flag beside the due
 *  label. Kept out of the meta line so a high-priority row scans on
 *  its right edge, the same place the eye already checks for dates. */
function PriorityFlag() {
  return (
    <span className="priority-flag" title="High priority">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M5 3v18h2v-7h11l-3-4 3-4H7V3z" />
      </svg>
      <span className="sr-only">High priority</span>
    </span>
  );
}

function formatOccasionMeta(o: ObligationDTO): React.ReactNode {
  // For anniversaries we prefer the user-supplied label ("Wedding",
  // "Partnership", "Met") when set, falling back to the generic
  // "Anniversary" otherwise. Birthdays stay simple.
  const label =
    o.occasionType === 'anniversary'
      ? o.occasionLabel?.trim() || 'Anniversary'
      : 'Birthday';
  // Years-from-anchor uses the next occurrence's year so "turns 65" is
  // the age they'll be on that birthday, not their current age.
  const nextYear = o.nextDueDate ? Number(o.nextDueDate.slice(0, 4)) : null;
  const years = nextYear && o.anchorYear ? nextYear - o.anchorYear : null;
  if (years === null || years <= 0) return <>{label}</>;
  if (o.occasionType === 'anniversary') {
    return (
      <>
        {label} · {years} {years === 1 ? 'year' : 'years'}
      </>
    );
  }
  return (
    <>
      {label} · turns {years}
    </>
  );
}

function formatDueLabel(o: ObligationDTO): string {
  if (o.status === 'completed') {
    return 'Done';
  }
  const due = o.nextDueDate ?? o.dueDate;
  if (!due) return '—';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${due.slice(0, 10)}T00:00:00`);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  // Time-of-day appends to the relative date label only when set
  // (most rows are all-day). Within the next day or two, `today 3pm`
  // / `tomorrow 9:00 am` reads naturally; further out we keep the
  // compact relative label and trust the preview card for the full
  // time prose.
  const timeSuffix = o.dueTime ? ` ${formatTimeForDisplay(o.dueTime)}` : '';
  if (diff === 0) return `today${timeSuffix}`;
  if (diff === 1) return `tomorrow${timeSuffix}`;
  if (diff === -1) return `yesterday${timeSuffix}`;
  if (diff < 0) return `${-diff}d overdue`;
  if (diff < 7) return `in ${diff}d${timeSuffix}`;
  if (diff < 30) return `in ${Math.round(diff / 7)}w`;
  if (diff < 365) return `in ${Math.round(diff / 30)}mo`;
  return `in ${Math.round(diff / 365)}y`;
}

function relativeClass(o: ObligationDTO, now: number): string {
  if (o.status === 'completed') return 'list-item__action--done';
  const due = o.nextDueDate ?? o.dueDate;
  if (!due) return '';
  // Time-aware overdue: timed tasks flip overdue the moment their
  // HH:MM crosses; untimed tasks stay current until end of day (the
  // 23:59 fallback inside isOverdue). Day-overdue items naturally
  // satisfy this same predicate, so the date-only branch from the
  // earlier version isn't needed separately.
  if (isOverdue(o, now)) return 'list-item__action--overdue';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${due.slice(0, 10)}T00:00:00`);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 7) return 'list-item__action--soon';
  return '';
}
